import { FEED_FILTER_AUTO_PAGE_MAX } from '@/constants/feed-pagination.js';
import {
  FEED_CRYPTO_MINUTE_CATEGORY_KEY,
  isCryptoFeedCategoryKey,
  isSportFeedRow,
} from '@/domain/market/feed-list-category.js';
import { resolveFeedPaginationMeta } from '@/domain/market/feed-source-adapter.js';
import {
  normalizeCryptoMinuteUpdownFeedRows,
  parseMinuteUpdownSlug,
  resolveUpdownFeedIntervalSec,
  resolveUpdownRoundSlugSource,
} from '@/domain/crypto/crypto-minute-slug.js';
import { resolveCollectionSlug } from '@/domain/market/collection-slug.js';

/**
 * 首页 Feed 需要分钟盘归集（与后端 groupBySeries 对齐）的 tab：
 * 「全部 / 加密分钟盘 / API Crypto」；其它 API 分类保持原始分页与原 silent 刷新。
 */
export function shouldNormalizeHomeFeedCategory(primaryCategory) {
  const category = String(primaryCategory ?? 'all').trim();
  if (!category) return false;
  if (category === 'all' || category === FEED_CRYPTO_MINUTE_CATEGORY_KEY) return true;
  return isCryptoFeedCategoryKey(category);
}

/**
 * 首页展示归集：分钟盘按 seriesKey 去重（优先 live）；加密分钟盘 tab 另排除体育行。
 * @param {object[]} rows
 * @param {string} [primaryCategory]
 * @param {number} [nowSec]
 */
export function normalizeHomeFeedRows(rows, primaryCategory, nowSec) {
  const category = String(primaryCategory ?? 'all').trim();
  const list = Array.isArray(rows) ? rows : [];
  const options = { nowSec };
  if (category === FEED_CRYPTO_MINUTE_CATEGORY_KEY) {
    return normalizeCryptoMinuteUpdownFeedRows(
      list.filter((row) => !isSportFeedRow(row)),
      options
    );
  }
  return normalizeCryptoMinuteUpdownFeedRows(list, options);
}

/**
 * 首页「全部」是否应隐藏该行：仅非 BTC 的 5m interval updown；
 * 其它周期 / 日盘 / 小时盘 / 非 updown 均不变。仅供 Home 展示层使用。
 * @param {object} row
 */
export function isHomeHiddenIntervalUpdownRow(row) {
  const slug = resolveMinuteUpdownSlugFromFeedRow(row);
  const parsed = parseMinuteUpdownSlug(slug);
  if (!parsed || parsed.kind !== 'interval') return false;
  if (String(parsed.interval ?? '').toLowerCase() !== '5m') return false;
  return String(parsed.asset ?? '')
    .trim()
    .toLowerCase() !== 'btc';
}

function resolveMinuteUpdownSlugFromFeedRow(row) {
  return resolveUpdownRoundSlugSource(row) || resolveCollectionSlug(row);
}

function resolveNowSec(nowSec) {
  return Number.isFinite(Number(nowSec)) && Number(nowSec) > 0
    ? Math.floor(Number(nowSec))
    : Math.floor(Date.now() / 1000);
}

/**
 * Feed 行是否为已结束的 interval 分钟/小时 updown（不含日盘长周期）。
 * @param {object} row
 * @param {number} [nowSec]
 */
export function isEndedIntervalUpdownFeedRow(row, nowSec = Math.floor(Date.now() / 1000)) {
  const slug = resolveMinuteUpdownSlugFromFeedRow(row);
  const parsed = parseMinuteUpdownSlug(slug);
  if (!parsed || parsed.kind === 'daily') return false;

  const now = Number(nowSec);
  const start = Number(parsed.roundTs);
  if (Number.isFinite(start) && start > 0 && Number.isFinite(now) && now > 0) {
    const intervalSec = resolveUpdownFeedIntervalSec(parsed.interval || parsed.seriesKey);
    const endSec = start + Math.max(Number(intervalSec) || 300, 60);
    if (now >= endSec) return true;
  }

  const market = Array.isArray(row?.markets) ? row.markets[0] : null;
  if (Number(row?.closed ?? market?.closed) === 1) return true;
  if (row?.active === 0 || row?.active === '0' || market?.active === 0 || market?.active === '0') {
    return true;
  }
  const endMs = Date.parse(String(row?.endDate ?? market?.endDate ?? ''));
  if (Number.isFinite(endMs) && Number.isFinite(now) && now * 1000 > endMs) return true;
  return false;
}

/**
 * 列表是否含已结束的 interval 分钟/小时 updown（需 silent refetch 换真实 live id）。
 * @param {object[]} rows
 * @param {number} [nowSec]
 */
export function feedRowsNeedIntervalUpdownLiveRefetch(
  rows,
  nowSec = Math.floor(Date.now() / 1000)
) {
  const list = Array.isArray(rows) ? rows : [];
  const now = resolveNowSec(nowSec);
  return list.some((row) => isEndedIntervalUpdownFeedRow(row, now));
}

/**
 * 禁止「只改 slug/title、保留旧 id」的假滚盘——点进去会 get 到上一档。
 * 已结束行保持原样；由 Feed silent tick 发现后 refetch page 换真实 live collection。
 * @param {object} row
 * @param {number} [nowSec]
 */
export function rollForwardIntervalUpdownFeedRow(row, _nowSec = Math.floor(Date.now() / 1000)) {
  return row;
}

/**
 * 原位置处理已结束分钟盘 → 滚到 live 轮次（保持列表顺序与长度）。
 * @param {object[]} rows
 * @param {string} [_primaryCategory]
 * @param {number} [nowSec]
 */
export function rearrangeHomeMinuteUpdownFeedRows(
  rows,
  _primaryCategory,
  nowSec = Math.floor(Date.now() / 1000)
) {
  const list = Array.isArray(rows) ? rows : [];
  const now = resolveNowSec(nowSec);
  return list.map((row) => rollForwardIntervalUpdownFeedRow(row, now));
}

/**
 * 加密页：原位置滚到 live 轮次（不删卡、不打接口）。
 * @param {object[]} rows
 * @param {number} [nowSec]
 */
export function rearrangeCryptoMinuteUpdownFeedRows(rows, nowSec = Math.floor(Date.now() / 1000)) {
  const list = Array.isArray(rows) ? rows : [];
  const now = resolveNowSec(nowSec);
  return list.map((row) => rollForwardIntervalUpdownFeedRow(row, now));
}

/**
 * 客户端分钟盘归集后不足 pageSize 时，连拉后续页直到展示条数够或无更多。
 * 返回的 apiRows 始终为「归集后的完整列表」（含 baseline），需配合 replaceRows 写入 store。
 *
 * @param {object} params
 * @param {number} params.pageSize
 * @param {number} params.startPageNum
 * @param {boolean} params.append
 * @param {object[]} [params.baselineRows] append 时已有（已归集）列表
 * @param {(rows: object[]) => object[]} params.normalizeRows
 * @param {(pageNum: number) => Promise<{ data: object, rows: object[] }>} params.fetchRawPage
 * @param {number} [params.maxPages]
 */
export async function fetchPagesUntilUpdownNormalized({
  pageSize,
  startPageNum,
  append,
  baselineRows = [],
  normalizeRows,
  fetchRawPage,
  maxPages = FEED_FILTER_AUTO_PAGE_MAX,
}) {
  const baseline = Array.isArray(baselineRows) ? baselineRows : [];
  const baselineDisplayCount = append ? normalizeRows(baseline).length : 0;
  const targetDisplayCount = append ? baselineDisplayCount + pageSize : pageSize;

  const accumulatedRaw = [];
  let pageNum = Math.max(1, Number(startPageNum) || 1);
  let hasMore = false;
  let pagesFetched = 0;

  while (pagesFetched < maxPages) {
    const { data, rows } = await fetchRawPage(pageNum);
    const list = Array.isArray(rows) ? rows : [];
    accumulatedRaw.push(...list);
    pagesFetched += 1;

    const pageMeta = resolveFeedPaginationMeta({
      data,
      rows: list,
      pageSize,
      rawRowsLength: 0,
      append: false,
    });
    hasMore = pageMeta.hasMore;

    const merged = normalizeRows(append ? [...baseline, ...accumulatedRaw] : accumulatedRaw);
    const filled = merged.length >= targetDisplayCount;
    const exhausted = !hasMore || list.length < pageSize;

    if (filled || exhausted) {
      return {
        apiRows: merged,
        nextPage: pageNum,
        hasMore: exhausted ? false : hasMore,
        replaceRows: true,
      };
    }

    pageNum += 1;
  }

  const merged = normalizeRows(append ? [...baseline, ...accumulatedRaw] : accumulatedRaw);
  return {
    apiRows: merged,
    nextPage: pageNum,
    hasMore,
    replaceRows: true,
  };
}
